/**
 * Computation Graph Type System
 *
 * A reactive computation graph where every value is either:
 * - A Source (raw data with citation)
 * - An Assumption (user-adjustable input)
 * - A Computed value (derived from other values)
 */

// Node identification
export type NodeId = string;

// Citation for provenance tracking
export interface Citation {
  source: string;
  url?: string;
  description?: string;
  accessedDate?: string;
}

// Units for formatting and display
export type Unit = 'currency' | 'percent' | 'ratio' | 'years' | 'number';

// Base properties shared by all nodes
interface BaseNode {
  id: NodeId;
  label: string;
  description?: string;
  unit: Unit;
}

/**
 * Source Node
 * Represents raw data with a citation - the "ground truth" values
 * Examples: tax rates from IRS, Zillow estimates, historical returns
 */
export interface SourceNode extends BaseNode {
  kind: 'source';
  value: number;
  citation: Citation;
}

/**
 * Preset option for an assumption
 * Allows switching between pre-defined scenarios
 */
export interface PresetOption {
  value: number;
  label: string;
  description?: string;
  citation?: Citation;
}

/**
 * Assumption Node
 * User-adjustable input with optional range constraints and presets
 * Examples: property value, expected appreciation rate, selling costs %
 */
export interface AssumptionNode extends BaseNode {
  kind: 'assumption';
  defaultValue: number;
  range?: {
    min: number;
    max: number;
    step?: number;
  };
  presets?: Record<string, PresetOption>;
  // If this assumption defaults to a source node's value
  defaultSource?: NodeId;
}

/**
 * Series Pattern Types
 * Defines the mathematical pattern for temporal sequences
 */
export type SeriesPatternType =
  | 'compound-growth' // value = initial × (1 + rate)^year
  | 'cumulative-sum' // value = sum(source[1..year])
  | 'portfolio-growth' // value = prev × (1 + return) + contribution
  | 'temporal-computed' // value = f(deps[year]) for each year
  | 'custom'; // user-defined pattern

/**
 * Series Metadata
 * Attached to each node in a temporal series to identify its membership
 */
export interface SeriesMeta {
  // The series this node belongs to (e.g., "propertyValue")
  seriesId: string;
  // The pattern type
  pattern: SeriesPatternType;
  // Which year in the series (0-10)
  year: number;
  // Total years in the series
  totalYears: number;
  // Human-readable label for the series (without year suffix)
  seriesLabel: string;
}

/**
 * Computed Node
 * Derived from other nodes via a compute function
 * Includes human-readable formula for display
 */
export interface ComputedNode extends BaseNode {
  kind: 'computed';
  deps: NodeId[];
  compute: (...values: number[]) => number;
  // Human-readable formula with placeholders: "{grossRent} - {expenses}"
  formula: string;
  // If this node is part of a temporal series
  seriesMeta?: SeriesMeta;
}

// Union type for all node kinds
export type Node = SourceNode | AssumptionNode | ComputedNode;

/**
 * Graph Definition
 * The complete specification of a computation graph
 */
export interface GraphDefinition {
  id: string;
  name: string;
  description?: string;
  nodes: Record<NodeId, Node>;
}

/**
 * Derivation Tree
 * Shows how a value was computed, recursively
 */
export interface DerivationTree {
  nodeId: NodeId;
  node: Node;
  value: number;
  // For computed nodes, the child derivations
  children: DerivationTree[];
  // Formula with actual values substituted: "$83,760 - $45,000 = $38,760"
  formulaWithValues?: string;
}

/**
 * Graph Instance
 * A live instance of a graph with cached values and overrides
 */
export interface Graph {
  definition: GraphDefinition;
  // Computed values cache
  values: Map<NodeId, number>;
  // User overrides for assumption nodes
  overrides: Map<NodeId, number>;
  // Reverse dependency index (node -> nodes that depend on it)
  dependents: Map<NodeId, Set<NodeId>>;
  // Topologically sorted node order for computation
  computeOrder: NodeId[];
}

// Type guards
export function isSourceNode(node: Node): node is SourceNode {
  return node.kind === 'source';
}

export function isAssumptionNode(node: Node): node is AssumptionNode {
  return node.kind === 'assumption';
}

export function isComputedNode(node: Node): node is ComputedNode {
  return node.kind === 'computed';
}

// Builder helpers for cleaner graph definitions
export function source(
  id: NodeId,
  value: number,
  config: Omit<SourceNode, 'kind' | 'id' | 'value'>
): SourceNode {
  return { kind: 'source', id, value, ...config };
}

export function assumption(
  id: NodeId,
  defaultValue: number,
  config: Omit<AssumptionNode, 'kind' | 'id' | 'defaultValue'>
): AssumptionNode {
  return { kind: 'assumption', id, defaultValue, ...config };
}

export function computed(
  id: NodeId,
  deps: NodeId[],
  compute: (...values: number[]) => number,
  config: Omit<ComputedNode, 'kind' | 'id' | 'deps' | 'compute'>
): ComputedNode {
  return { kind: 'computed', id, deps, compute, ...config };
}
